<template>
  <div class="candidate-files-page">
    <!-- Page Header -->
    <div class="page-header">
      <div class="breadcrumb-nav">
        <router-link :to="{ name: 'chatbot' }" class="breadcrumb-link">
          <i class="las la-home"></i>
          Financial Assistant
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <router-link :to="`/manage/chatbot/candidate/${candidateId}/sessions`" class="breadcrumb-link">
          Chat Sessions
        </router-link>
        <i class="las la-chevron-right breadcrumb-separator"></i>
        <span class="breadcrumb-current">Files</span>
      </div>
      
      <div class="header-content">
        <div class="header-info">
          <h1 class="page-title">Files for {{ candidateName }}</h1>
          <p class="page-subtitle">Manage and analyze financial documents for this candidate</p>
        </div>
        
        <!-- Action Buttons -->
        <div class="header-actions">
          <router-link :to="`/manage/chatbot/candidate/${candidateId}/upload`" class="upload-btn">
            <i class="las la-upload"></i>
            Upload New File
          </router-link>
        </div>
      </div>
    </div>
    
    <!-- File Type Filter Menu -->
    <div class="file-type-filter">
      <div class="filter-container">
        <h3 class="filter-title">Filter by Document Type</h3>
        <div class="filter-buttons">
          <button 
            v-for="option in fileTypeOptions" 
            :key="option.value"
            class="filter-btn"
            :class="{ active: selectedFileType === option.value }"
            @click.stop="selectFileType(option.value)"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
    </div>
    
    <!-- Main Content -->
    <div class="main-content">
      <div v-if="loading" class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading files...</p>
      </div>

      <div v-else-if="error" class="error-state">
        <p>{{ error }}</p>
        <button @click="loadFiles" class="retry-btn">Retry</button>
      </div>

      <div v-else-if="files.length === 0" class="empty-state">
        <div class="empty-icon">
          <i class="las la-file-pdf"></i>
        </div>
        <h3>No files found</h3>
        <p v-if="selectedFileType === 'all'">This candidate doesn't have any uploaded files yet.</p>
        <p v-else>No {{ getFileTypeLabel(selectedFileType) }} files found for this candidate.</p>
        <div class="empty-state-actions">
          <router-link :to="`/manage/chatbot/candidate/${candidateId}/upload`" class="upload-btn">
            <i class="las la-upload"></i>
            Upload First File
          </router-link>
        </div>
      </div>

      <div v-else class="files-content">
        <div class="files-header">
          <div class="header-actions-group">
            <button v-if="selectedFileType === 'bank_statement'" @click="validateAllBankStatements" class="validate-btn" :disabled="validatingBankStatement">
              <BaseSpinner v-if="validatingBankStatement" size="small" />
              <i v-else class="las la-check-circle"></i>
              {{ validatingBankStatement ? 'Validating...' : 'Validate All Bank Statements' }}
            </button>
            <button v-if="selectedFileType === 'bank_statement'" @click="generateCombinedReport" class="combined-report-btn" :disabled="generatingCombinedReport">
              <BaseSpinner v-if="generatingCombinedReport" size="small" />
              <i v-else class="las la-chart-bar"></i>
              {{ generatingCombinedReport ? 'Generating...' : 'Generate Combined Report' }}
            </button>
          </div>
        </div>

        <!-- Bulk Actions Bar -->
        <div v-if="selectedFiles.length > 0" class="bulk-actions-bar">
          <div class="bulk-actions-info">
            <span>{{ selectedFiles.length }} file{{ selectedFiles.length !== 1 ? 's' : '' }} selected</span>
          </div>
          <div class="bulk-actions-buttons">
            <button 
              @click="deleteSelectedFiles" 
              class="bulk-delete-btn" 
              :disabled="deletingFiles"
            >
              <BaseSpinner v-if="deletingFiles" size="small" />
              <i v-else class="las la-trash"></i>
              {{ deletingFiles ? 'Deleting...' : 'Delete Selected' }}
            </button>
            <button @click="clearSelection" class="bulk-cancel-btn">
              <i class="las la-times"></i>
              Cancel
            </button>
          </div>
        </div>

        <div class="files-table-wrapper">
          <table class="files-table">
            <thead>
              <tr>
                <th class="col-checkbox">
                  <label class="select-all-checkbox">
                    <input 
                      type="checkbox" 
                      :checked="allFilesSelected" 
                      @change="toggleSelectAll"
                    />
                  </label>
                </th>
                <th class="col-icon"></th>
                <th class="col-name">File Name</th>
                <th class="col-date">Uploaded</th>
                <th class="col-type">Type</th>
                <th class="col-status">Status</th>
                <th class="col-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="file in files" 
                :key="file.id || file.file_id || file.name" 
                class="file-row" 
                :class="{ 'selected': isFileSelected(file.id) }"
                @click.stop="toggleFileSelection(file)"
              >
                <td class="col-checkbox" @click.stop>
                  <label class="file-checkbox">
                    <input 
                      type="checkbox" 
                      :checked="isFileSelected(file.id)"
                      @change="toggleFileSelection(file)"
                    />
                  </label>
                </td>
                <td class="col-icon" @click.stop="viewFileDetails(file)">
                  <i :class="getFileIcon(file)"></i>
                </td>
                <td class="col-name" @click.stop="viewFileDetails(file)">
                  <span class="file-name">{{ formatName(file.name) }}</span>
                </td>
                <td class="col-date" @click.stop="viewFileDetails(file)">
                  <span class="file-date">{{ formatDate(file.scan_completed_at) }}</span>
                </td>
                <td class="col-type" @click.stop="viewFileDetails(file)">
                  <span class="file-type-badge">{{ formatType(file.type) }}</span>
                </td>
                <td class="col-status" @click.stop="viewFileDetails(file)">
                  <span class="file-status-badge" :class="getStatusClass(file.status)">{{ formatStatus(file.status) }}</span>
                </td>
                <td class="col-actions" @click.stop>
                  <div class="action-buttons-group">
                    <BaseSpinner 
                      v-if="file.type === 'bank_statement' && validatingFile === file.id" 
                      size="small" 
                      class="action-icon-spinner"
                    />
                    <i 
                      v-else-if="file.type === 'bank_statement'"
                      @click.stop="validateBankStatement(file)" 
                      class="las la-check-circle action-icon validate-icon" 
                      title="Validate Bank Statement"
                      :class="{ disabled: validatingFile === file.id || file.status === 'scanning' || file.status === 'extracting' || file.status === 'pending' }"
                    ></i>
                    <BaseSpinner 
                      v-if="shouldShowReportButton(file) && generatingReport === file.id" 
                      size="small" 
                      class="action-icon-spinner"
                    />
                    <i 
                      v-else-if="shouldShowReportButton(file)"
                      @click.stop="generateReport(file)" 
                      class="las la-chart-bar action-icon report-icon" 
                      title="Generate Report"
                      :class="{ disabled: generatingReport === file.id || file.status === 'scanning' || file.status === 'extracting' || file.status === 'pending' }"
                    ></i>
                    <i
                      v-if="file.type === 'credit_model'"
                      @click.stop="generateCreditModelReport(file)"
                      class="las la-file-pdf action-icon report-icon"
                      title="Generate Credit Model Report"
                      :class="{ disabled: file.status === 'scanning' || file.status === 'extracting' || file.status === 'pending' }"
                    ></i>
                    <BaseSpinner 
                      v-if="deletingFile === file.id" 
                      size="small" 
                      class="action-icon-spinner"
                    />
                    <i 
                      v-else
                      @click.stop="deleteFile(file)" 
                      class="las la-trash action-icon delete-icon" 
                      title="Delete File"
                      :class="{ disabled: deletingFile === file.id }"
                    ></i>
                    <i 
                      v-if="file.type === 'analysis_pack'"
                      @click.stop="matchFile(candidateId, { file_id: file.id })"
                      class="las la-search action-icon match-icon"
                      title="Match File"
                      :class="{ disabled: file.status === 'scanning' || file.status === 'extracting' || file.status === 'pending' }"
                    ></i>
                  </div>
                </td>
              </tr>
              <!-- Rescan Progress Bar Rows -->
              <tr 
                v-for="file in files" 
                v-if="rescanProgress[file.id] && rescanProgress[file.id].show"
                :key="`progress-${file.id || file.file_id || file.name}`" 
                class="progress-row"
              >
                <td colspan="7" class="progress-cell">
                  <div class="rescan-progress">
                    <div class="progress-bar">
                      <div class="progress-fill" :style="{ width: rescanProgress[file.id].percentage + '%' }"></div>
                    </div>
                    <div class="progress-text">
                      <span class="progress-message">{{ rescanProgress[file.id].message }}</span>
                      <span class="progress-percentage">{{ rescanProgress[file.id].percentage }}%</span>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <BasePagination
          v-if="totalRows > 0"
          :totalRows="totalRows"
          :activePage="currentPage"
          :pageSize="pageSize"
          @set-page="setPage"
        />

      </div>
    </div>
  </div>
</template>

<script>
import ChatbotService from '@/services/chatbot.service';
import BaseSpinner from '@/components/UI/BaseSpinner.vue';
import BasePagination from '@/components/UI/BasePagination.vue';
import { PAGE_SIZE } from '@/constants';

export default {
  name: 'CandidateFiles',
  components: {
    BaseSpinner,
    BasePagination
  },
  props: {
    candidateId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      files: [],
      loading: true,
      error: null,
      candidateName: '',
      deletingFile: null,
      generatingCombinedReport: false,
      validatingBankStatement: false,
      validatingFile: null,
      generatingReport: null,
      rescanProgress: {},
      selectedFiles: [],
      deletingFiles: false,
      selectedFileType: 'all',
      currentPage: 1,
      pageSize: PAGE_SIZE,
      totalRows: 0,
      fileTypeOptions: [
        { value: 'all', label: 'All Files' },
        { value: 'bank_statement', label: 'Bank Statement' },
        { value: 'multi_account_statement', label: 'Multi-Account Statement' },
        { value: 'emirates_id', label: 'Emirates ID' },
        { value: 'trade_license', label: 'Trade License' },
        { value: 'vat_statement', label: 'VAT Statement' },
        { value: 'aecb_report', label: 'AECB Report' },
        { value: 'payer_list', label: 'Payer List' },
        { value: 'credit_model', label: 'Credit Model' },
        { value: 'analysis_pack', label: 'Analysis Pack' }
      ]
    }
  },
  computed: {
    allFilesSelected() {
      return this.files.length > 0 && 
             this.selectedFiles.length === this.files.length;
    }
  },
  
  async mounted() {
    await this.loadCandidateInfo();
    await this.loadFiles();
  },
  methods: {
    selectFileType(fileType) {
      this.selectedFileType = fileType;
      this.currentPage = 1; // Reset to first page when filter changes
      this.loadFiles();
    },
    
    setPage(page, pageSize) {
      if (pageSize && pageSize !== this.pageSize) {
        this.pageSize = pageSize;
      }
      if (page >= 1) {
        this.currentPage = page;
        this.loadFiles();
      }
    },
    
    getFileTypeLabel(fileType) {
      const option = this.fileTypeOptions.find(opt => opt.value === fileType);
      return option ? option.label : fileType;
    },
    
    getFileIcon(file) {
      const documentType = file.document_type || file.type;
      
      switch (documentType) {
        case 'application/pdf':
          return 'las la-file-pdf';
        case 'pdf':
          return 'las la-file-pdf';
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
        case 'excel':
        case 'xlsx':
        case 'xls':
          return 'las la-file-excel';
      }
    },
    
    shouldShowReportButton(file) {
      const fileType = file.type;
      // Hide report button for payer list and credit model files
      return fileType !== 'payer_list' && fileType !== 'credit_model';
    },
    
    async loadCandidateInfo() {
      try {
        const candidate = await ChatbotService.getFinancingCandidate(this.candidateId);
        this.candidateName = candidate.name || candidate.company || 'Unknown Candidate';
      } catch (error) {
        console.error('Error loading candidate info:', error);
        this.candidateName = 'Unknown Candidate';
      }
    },
    async loadFiles() {
      this.loading = true;
      this.error = null;
      
      try {
        const params = {
          page: this.currentPage,
          page_size: this.pageSize
        };
        
        // Add file type filter if not 'all'
        if (this.selectedFileType !== 'all') {
          params.type = this.selectedFileType;
        }
        
        const filesData = await ChatbotService.getUploads(this.candidateId, params);

        // Handle different response formats
        let filesArray = [];
        if (Array.isArray(filesData)) {
          filesArray = filesData;
        } else if (filesData.data) {
          filesArray = filesData.data;
          // Update total rows if provided
          if (filesData.count !== undefined) {
            this.totalRows = filesData.count;
          }
        } else if (filesData.files) {
          filesArray = filesData.files;
          if (filesData.count !== undefined) {
            this.totalRows = filesData.count;
          }
        } else if (filesData.uploads) {
          filesArray = filesData.uploads;
          if (filesData.count !== undefined) {
            this.totalRows = filesData.count;
          }
        }
        
        this.files = filesArray.map(file => ({
          ...file,
          id: file.id,
          name: file.name || 'Unknown File',
          uploaded_at: file.uploaded_at || null,
          document_type: file.document_type || 'Unknown Type',
          status: file.scan_status || 'pending',
          type: file.type || 'Unknown Type'
        }));
        
        // If totalRows wasn't set from response, use files length as fallback
        // This handles cases where backend doesn't return count
        if (this.totalRows === 0 && this.files.length > 0) {
          // If we got a full page, there might be more
          if (this.files.length === this.pageSize) {
            this.totalRows = this.files.length * this.currentPage + 1; // Estimate
          } else {
            this.totalRows = (this.currentPage - 1) * this.pageSize + this.files.length;
          }
        }
        
        console.log('files', this.files);
        console.log('totalRows', this.totalRows);
        
      } catch (error) {
        console.error('Error loading files:', error);
        this.error = 'Failed to load files. Please try again.';
        this.files = [];
        this.totalRows = 0;
      } finally {
        this.loading = false;
      }
    },

    viewFileDetails(file) {
      const fileId = file.id || file.file_id;
      if (fileId) {
        this.$router.push({
          name: 'chatbot-file-details',
          params: {
            candidateId: this.candidateId,
            fileId: fileId
          }
        });
      } else {
        console.error('No file ID found for file:', file);
        this.$bvToast.toast('Unable to view file details. File ID not found.', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      }
    },

    formatName(name) {
      return name.split('.')[0];
    },
    formatType(type) {
      return type.toUpperCase().replaceAll('_', ' ');
    },
    formatStatus(status) {
      switch (status) {
        case 'scanning':
          return 'SCANNING';
        case 'processing':
          return 'PROCESSING';
        case 'extracting':
          return 'EXTRACTING';
        case 'indexing':
          return 'INDEXING';
        case 'completed':
          return 'COMPLETED';
        case 'pending':
          return 'PENDING';
        default:
          return 'UNKNOWN';
      }
    },
    
    getStatusClass(status) {
      switch (status) {
        case 'completed':
          return 'status-completed';
        case 'scanning':
        case 'processing':
        case 'extracting':
        case 'indexing':
          return 'status-processing';
        case 'pending':
          return 'status-pending';
        default:
          return 'status-unknown';
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return 'Invalid date';
        
        const now = new Date();
        const diffTime = Math.abs(now - date);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 1) return 'Today';
        if (diffDays === 2) return 'Yesterday';
        if (diffDays <= 7) return `${diffDays - 1} days ago`;
        
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch (error) {
        console.error('Error formatting date:', error);
        return 'Invalid date';
      }
    },
    async generateReport(file) {
      const fileId = file.id;
      if (!fileId) {
        this.$bvToast.toast('Error: Could not determine file ID', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.generatingReport = fileId;
      
      try {
        const blob = await ChatbotService.generateReport(fileId);
        
        // Ensure we have a proper Blob object
        if (!(blob instanceof Blob)) {
          throw new Error('Invalid report data received from server');
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${file.name || 'report'}_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating report:', error);
        this.$bvToast.toast('Error generating report: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generatingReport = null;
      }
    },

    generateCreditModelReport(file) {
      const fileId = file.id;
      if (!fileId) {
        this.$bvToast.toast('Error: Could not determine file ID', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      // Navigate to report details page
      this.$router.push({
        name: 'chatbot-report-details',
        params: {
          candidateId: this.candidateId,
          fileId: fileId
        },
        query: {
          reportType: 'credit_model'
        }
      });
    },
    async matchFile(candidateId, payload) {
      const response = await ChatbotService.matchFile(candidateId, payload);
      console.log('response', response);
      if (response.status === 'success') {
        this.$bvToast.toast('File matched successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } else {
        this.$bvToast.toast('Error matching file: ' + response.message, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      }
      await this.loadFiles();
    },
    isFileSelected(fileId) {
      return this.selectedFiles.some(f => f.id === fileId);
    },
    
    toggleFileSelection(file) {
      const index = this.selectedFiles.findIndex(f => f.id === file.id);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
      } else {
        this.selectedFiles.push(file);
      }
    },
    
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedFiles = [...this.files];
      } else {
        this.selectedFiles = [];
      }
    },
    
    clearSelection() {
      this.selectedFiles = [];
    },
    
    async deleteSelectedFiles() {
      if (this.selectedFiles.length === 0) return;
      
      // Show confirmation dialog
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete ${this.selectedFiles.length} file${this.selectedFiles.length !== 1 ? 's' : ''}? This action cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) {
        return;
      }

      this.deletingFiles = true;
      const filesToDelete = [...this.selectedFiles];
      
      try {
        // Delete all selected files
        const deletePromises = filesToDelete.map(file => {
          const fileId = file.id;
          if (!fileId) {
            console.warn('Skipping file without ID:', file);
            return Promise.resolve();
          }
          return ChatbotService.deleteFile(fileId);
        });
        
        await Promise.all(deletePromises);
        
        this.$bvToast.toast(`${filesToDelete.length} file${filesToDelete.length !== 1 ? 's' : ''} deleted successfully!`, {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
        
        // Clear selection and refresh the file list
        this.selectedFiles = [];
        await this.loadFiles();
        
      } catch (error) {
        console.error('Error deleting files:', error);
        this.$bvToast.toast('Error deleting files: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.deletingFiles = false;
      }
    },

    async deleteFile(file) {
      const fileId = file.id;
      if (!fileId) {
        this.$bvToast.toast('Error: Could not determine file ID', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      // Show confirmation dialog
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Are you sure you want to delete "${file.name}"? This action cannot be undone.`,
        {
          title: 'Confirm Delete',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete',
          cancelTitle: 'Cancel',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: true
        }
      );

      if (!confirmed) {
        return;
      }

      this.deletingFile = fileId;
      try {
        // Call the delete service
        await ChatbotService.deleteFile(fileId);
        
        // Remove from selected files if it was selected
        const index = this.selectedFiles.findIndex(f => f.id === fileId);
        if (index > -1) {
          this.selectedFiles.splice(index, 1);
        }
        
        this.$bvToast.toast('File deleted successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
        
        // Refresh the file list
        await this.loadFiles();
        
      } catch (error) {
        console.error('Error deleting file:', error);
        this.$bvToast.toast('Error deleting file: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.deletingFile = null;
      }
    },
    
    showRescanProgress(fileId) {
      // Create or update progress bar for this file
      this.$set(this.rescanProgress, fileId, {
        show: true,
        percentage: 0,
        message: 'Starting rescan...'
      });
    },
    
    hideRescanProgress(fileId) {
      // Hide progress bar for this file
      this.$set(this.rescanProgress, fileId, {
        show: false,
        percentage: 0,
        message: ''
      });
    },
    
    async generateCombinedReport() {
      this.generatingCombinedReport = true;
      try {
        const blob = await ChatbotService.generateCombinedReport(this.candidateId);
        
        // Ensure we have a proper Blob object
        if (!(blob instanceof Blob)) {
          throw new Error('Invalid combined report data received from server');
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${this.candidateName || 'candidate'}_combined_report_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.$bvToast.toast('Combined report generated and downloaded successfully!', {
          title: 'Success',
          variant: 'success',
          solid: true,
          autoHideDelay: 3000
        });
      } catch (error) {
        console.error('Error generating combined report:', error);
        this.$bvToast.toast('Error generating combined report: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.generatingCombinedReport = false;
      }
    },
    
    async validateBankStatement(file) {
      const fileId = file.id;
      if (!fileId) {
        this.$bvToast.toast('Error: Could not determine file ID', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.validatingFile = fileId;
      try {
        const result = await ChatbotService.validateBankStatement(fileId, this.candidateId);
        
        // Handle validation result
        if (result.valid) {
          this.$bvToast.toast(result.message || 'Bank statement validation passed successfully!', {
            title: 'Validation Successful',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } else {
          // Show validation errors if any
          const errorMessage = result.errors 
            ? result.errors.join(', ') 
            : result.message || 'Bank statement validation failed';
            
          this.$bvToast.toast(errorMessage, {
            title: 'Validation Failed',
            variant: 'warning',
            solid: true,
            autoHideDelay: 8000
          });
        }
        
        // Refresh file list to show updated validation status
        await this.loadFiles();
      } catch (error) {
        console.error('Error validating bank statement:', error);
        this.$bvToast.toast('Error validating bank statement: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.validatingFile = null;
      }
    },
    
    async validateAllBankStatements() {
      const bankStatements = this.files.filter(file => file.type === 'bank_statement');
      
      if (bankStatements.length === 0) {
        this.$bvToast.toast('No bank statements found to validate.', {
          title: 'Info',
          variant: 'info',
          solid: true,
          autoHideDelay: 3000
        });
        return;
      }

      this.validatingBankStatement = true;
      let successCount = 0;
      let failureCount = 0;
      
      try {
        // Validate each bank statement
        for (const file of bankStatements) {
          try {
            const result = await ChatbotService.validateBankStatement(file.id, this.candidateId);
            if (result.valid) {
              successCount++;
            } else {
              failureCount++;
            }
          } catch (error) {
            console.error(`Error validating file ${file.id}:`, error);
            failureCount++;
          }
        }
        
        // Show summary
        const total = bankStatements.length;
        const message = `Validation complete: ${successCount} passed, ${failureCount} failed out of ${total} bank statements.`;
        
        this.$bvToast.toast(message, {
          title: 'Validation Complete',
          variant: failureCount === 0 ? 'success' : 'warning',
          solid: true,
          autoHideDelay: 6000
        });
        
        // Refresh file list
        await this.loadFiles();
      } catch (error) {
        console.error('Error validating bank statements:', error);
        this.$bvToast.toast('Error validating bank statements: ' + (error.message || 'Unknown error'), {
          title: 'Error',
          variant: 'danger',
          solid: true,
          autoHideDelay: 5000
        });
      } finally {
        this.validatingBankStatement = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import './candidateFiles.scss';
</style> 